import React, { useState, useEffect } from 'react';
import {
  Users,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Plus,
  RefreshCw,
  Search,
  FileSpreadsheet,
  ArrowDownRight,
  ArrowUpRight,
  DollarSign
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  coreInsuranceApi,
  CoreIntermediaryDto,
  BrokerStatementDto
} from '../api/coreInsuranceApi';

export default function CoreIntermediaries() {
  const [intermediaries, setIntermediaries] = useState<CoreIntermediaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Onboard Broker Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [brokerForm, setBrokerForm] = useState({
    code: 'BRK-REUNION-001',
    name: 'Reunion Insurance Brokers Ltd',
    type: 1, // Broker
    contactPerson: 'Kondwani Banda',
    email: 'kbanda@reunion.mw',
    phone: '+265 1 820 100',
    taxIdentificationNumber: 'TPIN-9921448',
    regulatoryLicenseNumber: 'RBM/INS/BRK/2026/04',
    creditPeriodDays: 30,
    creditLimit: 50000000,
    withholdingTaxRate: 0.10,
    motorCommissionRate: 0.10,
    propertyCommissionRate: 0.20,
    personalAccidentCommissionRate: 0.20,
    marineCommissionRate: 0.15
  });

  // Statement & Aging Modal
  const [selectedBroker, setSelectedBroker] = useState<CoreIntermediaryDto | null>(null);
  const [statement, setStatement] = useState<BrokerStatementDto | null>(null);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [loadingStatement, setLoadingStatement] = useState(false);

  // Remittance Modal
  const [showRemittanceModal, setShowRemittanceModal] = useState(false);
  const [remittanceAmount, setRemittanceAmount] = useState(5000000);
  const [bankRef, setBankRef] = useState('EFT-NBM-2026-99281');
  const [recordingRemittance, setRecordingRemittance] = useState(false);

  // Bordereau Upload & Audit Modal
  const [showBordereauModal, setShowBordereauModal] = useState(false);
  const [submittingBordereau, setSubmittingBordereau] = useState(false);
  const [bordereauYear, setBordereauYear] = useState(2026);
  const [bordereauMonth, setBordereauMonth] = useState(9);
  const [bordereauReconcileResult, setBordereauReconcileResult] = useState<any>(null);

  useEffect(() => {
    fetchIntermediaries();
  }, []);

  const fetchIntermediaries = async () => {
    setLoading(true);
    try {
      const data = await coreInsuranceApi.getIntermediaries();
      setIntermediaries(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load intermediaries.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBroker = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await coreInsuranceApi.createIntermediary(brokerForm);
      toast.success('Broker onboarded with commercial credit terms & WHT matrix!');
      setShowCreateModal(false);
      fetchIntermediaries();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to onboard broker.');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenStatement = async (broker: CoreIntermediaryDto) => {
    setSelectedBroker(broker);
    setShowStatementModal(true);
    setLoadingStatement(true);
    try {
      const data = await coreInsuranceApi.getIntermediaryStatement(broker.id);
      setStatement(data);
    } catch (err) {
      toast.error('Failed to fetch broker sub-ledger statement.');
    } finally {
      setLoadingStatement(false);
    }
  };

  const handleRecordRemittance = async () => {
    if (!selectedBroker) return;
    setRecordingRemittance(true);
    try {
      await coreInsuranceApi.recordRemittance(selectedBroker.id, {
        amount: remittanceAmount,
        bankTransactionReference: bankRef,
        paymentChannel: 'National Bank Direct Transfer'
      });
      toast.success('Remittance credited to broker sub-ledger!');
      setShowRemittanceModal(false);
      fetchIntermediaries();
      if (showStatementModal) {
        handleOpenStatement(selectedBroker);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Remittance recording failed.');
    } finally {
      setRecordingRemittance(false);
    }
  };

  const filtered = intermediaries.filter(i =>
    i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="text-emerald-600 dark:text-emerald-400" />
            Intermediary & Broker Sub-Ledger
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Broker credit control, 10% statutory WHT deductions, double-entry sub-ledger, and aged debt analysis.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition"
          >
            <Plus size={18} />
            Onboard Broker
          </button>
          <button
            onClick={fetchIntermediaries}
            className="p-2 bg-white dark:bg-slate-800 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Registered Intermediaries</span>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{intermediaries.length}</div>
          <span className="text-xs text-blue-500 font-medium">Brokers & Distribution Agents</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Outstanding Premium Receivables</span>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
            MWK {(intermediaries.reduce((acc, i) => acc + (i.currentOutstandingBalance || 0), 0) / 1000000).toFixed(2)}M
          </div>
          <span className="text-xs text-emerald-600 font-medium">Net debtor sub-ledger balance</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Aggregate Credit Facility</span>
          <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
            MWK {(intermediaries.reduce((acc, i) => acc + (i.creditLimit || 0), 0) / 1000000).toFixed(0)}M
          </div>
          <span className="text-xs text-indigo-500 font-medium">Approved underwriting capacity</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Underwriting Suspensions</span>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
            {intermediaries.filter(i => i.isUnderwritingSuspended).length}
          </div>
          <span className="text-xs text-red-500 font-medium">Breached credit limits</span>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by broker name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-800 border rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-600 dark:text-gray-300 font-semibold border-b">
              <tr>
                <th className="py-3.5 px-4">Intermediary</th>
                <th className="py-3.5 px-4">Credit Period</th>
                <th className="py-3.5 px-4">Credit Limit</th>
                <th className="py-3.5 px-4">Current Balance</th>
                <th className="py-3.5 px-4">Commission & WHT</th>
                <th className="py-3.5 px-4">Credit Status</th>
                <th className="py-3.5 px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    <RefreshCw className="animate-spin inline mr-2" size={18} /> Loading broker sub-ledger...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    No intermediaries found. Click "Onboard Broker" to add a distribution partner.
                  </td>
                </tr>
              ) : (
                filtered.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/70 dark:hover:bg-slate-800/40 transition">
                    <td className="py-4 px-4">
                      <div className="font-semibold text-gray-900 dark:text-white">{item.name}</div>
                      <div className="text-xs text-emerald-600 font-mono">{item.code}</div>
                    </td>
                    <td className="py-4 px-4 text-gray-600 dark:text-gray-300">
                      {item.creditPeriodDays} Days
                    </td>
                    <td className="py-4 px-4 font-mono font-medium text-gray-800 dark:text-gray-200">
                      MWK {item.creditLimit?.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 font-mono font-bold text-emerald-600 dark:text-emerald-400">
                      MWK {item.currentOutstandingBalance?.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-xs text-gray-500">
                      <div>Motor: {((item.motorCommissionRate || 0) * 100).toFixed(0)}%</div>
                      <div>WHT: {((item.withholdingTaxRate || 0) * 100).toFixed(0)}%</div>
                    </td>
                    <td className="py-4 px-4">
                      {item.isUnderwritingSuspended ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300">
                          <AlertTriangle size={12} /> Stopped
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                          <CheckCircle size={12} /> In Good Standing
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenStatement(item)}
                          className="px-2 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded text-xs hover:bg-emerald-100 font-medium"
                        >
                          Aged Statement
                        </button>
                        <button
                          onClick={() => {
                            setSelectedBroker(item);
                            setShowRemittanceModal(true);
                          }}
                          className="px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded text-xs hover:bg-blue-100 font-medium"
                        >
                          Remit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedBroker(item);
                            setBordereauReconcileResult(null);
                            setShowBordereauModal(true);
                          }}
                          className="px-2 py-1 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded text-xs hover:bg-amber-100 font-medium"
                        >
                          Bordereau
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* AGED DEBT STATEMENT & SUB-LEDGER MODAL */}
      {showStatementModal && selectedBroker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-4xl w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileSpreadsheet className="text-emerald-600" />
                  Broker Sub-Ledger & Aged Debt Statement
                </h3>
                <p className="text-xs text-gray-500">
                  {selectedBroker.name} ({selectedBroker.code}) | Credit Limit: MWK {selectedBroker.creditLimit?.toLocaleString()}
                </p>
              </div>
              <button onClick={() => setShowStatementModal(false)} className="text-xl font-bold text-gray-400">
                &times;
              </button>
            </div>

            {loadingStatement ? (
              <div className="py-8 text-center text-gray-400">Loading sub-ledger transactions...</div>
            ) : statement ? (
              <div className="space-y-4 text-xs">
                {/* 4 AGING BUCKETS */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-200 dark:border-emerald-800">
                    <span className="text-gray-500 font-medium">0 - 30 Days (Current)</span>
                    <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300 mt-1">
                      MWK {statement.summary?.aging?.current_0_To_30_Days?.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-xl border border-blue-200 dark:border-blue-800">
                    <span className="text-gray-500 font-medium">31 - 60 Days (Overdue)</span>
                    <div className="text-lg font-bold text-blue-700 dark:text-blue-300 mt-1">
                      MWK {statement.summary?.aging?.pastDue_31_To_60_Days?.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-800">
                    <span className="text-gray-500 font-medium">61 - 90 Days (Critical)</span>
                    <div className="text-lg font-bold text-amber-700 dark:text-amber-300 mt-1">
                      MWK {statement.summary?.aging?.pastDue_61_To_90_Days?.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-950/40 rounded-xl border border-red-200 dark:border-red-800">
                    <span className="text-gray-500 font-medium">Over 90 Days (Default)</span>
                    <div className="text-lg font-bold text-red-700 dark:text-red-300 mt-1">
                      MWK {statement.summary?.aging?.over_90_Days?.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* TRANSACTIONS TABLE */}
                <div className="border rounded-xl overflow-hidden mt-4">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-gray-50 dark:bg-slate-800 text-gray-600 font-semibold border-b">
                      <tr>
                        <th className="py-2.5 px-3">Date</th>
                        <th className="py-2.5 px-3">Type</th>
                        <th className="py-2.5 px-3">Reference & Details</th>
                        <th className="py-2.5 px-3 text-right">Debit (MWK)</th>
                        <th className="py-2.5 px-3 text-right">Credit (MWK)</th>
                        <th className="py-2.5 px-3 text-right">Running Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {statement.transactions?.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="py-2 px-3">{new Date(t.transactionDate).toLocaleDateString()}</td>
                          <td className="py-2 px-3 font-semibold text-blue-600">{t.transactionType}</td>
                          <td className="py-2 px-3">
                            <div className="font-mono font-medium">{t.referenceNumber}</div>
                            <div className="text-[10px] text-gray-400">{t.description}</div>
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-semibold text-red-600">
                            {t.debitAmount > 0 ? t.debitAmount.toLocaleString() : '-'}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-semibold text-green-600">
                            {t.creditAmount > 0 ? t.creditAmount.toLocaleString() : '-'}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-bold">
                            MWK {t.runningBalance.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* REMITTANCE MODAL */}
      {showRemittanceModal && selectedBroker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <CreditCard className="text-blue-600" />
              Record Broker Remittance Settlement
            </h3>
            <p className="text-xs text-gray-500">
              Credits broker sub-ledger to reduce outstanding premium receivables for <span className="font-semibold">{selectedBroker.name}</span>.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Remittance Amount (MWK)</label>
                <input
                  type="number"
                  value={remittanceAmount}
                  onChange={(e) => setRemittanceAmount(parseFloat(e.target.value))}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono font-bold"
                />
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Bank Reference / EFT #</label>
                <input
                  type="text"
                  value={bankRef}
                  onChange={(e) => setBankRef(e.target.value)}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono font-medium"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setShowRemittanceModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordRemittance}
                disabled={recordingRemittance}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-sm"
              >
                {recordingRemittance ? 'Recording...' : 'Credit Broker Ledger'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ONBOARD BROKER MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full border shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Plus className="text-emerald-600" />
              Onboard Commercial Intermediary / Broker
            </h3>
            <p className="text-xs text-gray-500">Configure regulatory license, credit limit, and commission matrix.</p>

            <form onSubmit={handleCreateBroker} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium mb-1">Broker Code</label>
                  <input
                    type="text"
                    value={brokerForm.code}
                    onChange={(e) => setBrokerForm({ ...brokerForm, code: e.target.value })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Company Name</label>
                  <input
                    type="text"
                    value={brokerForm.name}
                    onChange={(e) => setBrokerForm({ ...brokerForm, name: e.target.value })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium mb-1">Credit Limit (MWK)</label>
                  <input
                    type="number"
                    value={brokerForm.creditLimit}
                    onChange={(e) => setBrokerForm({ ...brokerForm, creditLimit: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Credit Period (Days)</label>
                  <input
                    type="number"
                    value={brokerForm.creditPeriodDays}
                    onChange={(e) => setBrokerForm({ ...brokerForm, creditPeriodDays: parseInt(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-medium mb-1">Motor Comm. (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={brokerForm.motorCommissionRate}
                    onChange={(e) => setBrokerForm({ ...brokerForm, motorCommissionRate: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Property Comm. (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={brokerForm.propertyCommissionRate}
                    onChange={(e) => setBrokerForm({ ...brokerForm, propertyCommissionRate: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Statutory WHT (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={brokerForm.withholdingTaxRate}
                    onChange={(e) => setBrokerForm({ ...brokerForm, withholdingTaxRate: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-sm"
                >
                  {creating ? 'Saving...' : 'Register Intermediary'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BORDEREAU UPLOAD & AUDIT RECONCILIATION MODAL */}
      {showBordereauModal && selectedBroker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full border shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileSpreadsheet className="text-amber-600" />
                  Broker Bordereau Submission & Automated Audit
                </h3>
                <p className="text-xs text-gray-500">
                  Submit monthly policy schedule for <span className="font-semibold">{selectedBroker.name}</span> ({selectedBroker.code}) to reconcile commission deductions and net remittances.
                </p>
              </div>
              <button onClick={() => setShowBordereauModal(false)} className="text-xl font-bold text-gray-400">
                &times;
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Audit Year</label>
                <input
                  type="number"
                  value={bordereauYear}
                  onChange={(e) => setBordereauYear(parseInt(e.target.value))}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Accounting Month (1-12)</label>
                <input
                  type="number"
                  value={bordereauMonth}
                  onChange={(e) => setBordereauMonth(parseInt(e.target.value))}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs space-y-2">
              <div className="font-semibold text-slate-800 dark:text-slate-200">Simulated Schedule Extraction (Marsh / Minet Batch)</div>
              <div className="text-slate-500">
                - Policy 1: POL-MOT-2026-AUT-101 (GWP: MWK 2,250,000 | 10% Comm: MWK 225,000 | WHT: MWK 22,500)<br />
                - Policy 2: POL-MOT-2026-AUT-102 (GWP: MWK 1,575,000 | 10% Comm: MWK 157,500 | WHT: MWK 15,750)
              </div>
              <div className="font-bold text-emerald-600 dark:text-emerald-400">
                Total Gross Premium: MWK 3,825,000.00 | Net Remittance: MWK 3,480,750.00
              </div>
            </div>

            {bordereauReconcileResult && (
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 rounded-xl border border-emerald-300 dark:border-emerald-800 text-xs space-y-2">
                <div className="font-bold text-emerald-800 dark:text-emerald-200 flex items-center gap-1.5">
                  <CheckCircle size={16} /> Bordereau Audited & Reconciled Successfully!
                </div>
                <div className="text-emerald-700 dark:text-emerald-300">
                  Bordereau Ref: <span className="font-mono font-bold">{bordereauReconcileResult.bordereauNumber}</span><br />
                  Matched Policies: {bordereauReconcileResult.matchedPoliciesCount || 2} / {bordereauReconcileResult.totalPoliciesCount || 2}<br />
                  Audit Status: <span className="font-semibold uppercase">{bordereauReconcileResult.status || 'Reconciled'}</span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                type="button"
                onClick={() => setShowBordereauModal(false)}
                className="px-4 py-2 text-xs text-gray-600 hover:text-gray-900"
              >
                Close
              </button>
              <button
                type="button"
                disabled={submittingBordereau}
                onClick={async () => {
                  setSubmittingBordereau(true);
                  try {
                    const sampleSchedule = {
                      intermediaryId: selectedBroker.id,
                      year: bordereauYear,
                      month: bordereauMonth,
                      items: [
                        {
                          policyNumber: 'POL-MOT-2026-AUT-101',
                          policyholderName: 'National Haulage Ltd',
                          lineOfBusiness: 1,
                          grossWrittenPremium: 2250000,
                          commissionRate: 0.10,
                          commissionAmount: 225000,
                          withholdingTaxAmount: 22500
                        },
                        {
                          policyNumber: 'POL-MOT-2026-AUT-102',
                          policyholderName: 'Central Logistics Ltd',
                          lineOfBusiness: 1,
                          grossWrittenPremium: 1575000,
                          commissionRate: 0.10,
                          commissionAmount: 157500,
                          withholdingTaxAmount: 15750
                        }
                      ]
                    };
                    const res = await coreInsuranceApi.submitBrokerBordereau(sampleSchedule);
                    toast.success('Bordereau schedule uploaded! Running automated audit...');
                    if (res && res.id) {
                      const rec = await coreInsuranceApi.reconcileBrokerBordereau(res.id, 'Underwriting Auditor');
                      setBordereauReconcileResult(rec);
                      toast.success('Bordereau reconciled against core PAS ledger!');
                    } else {
                      setBordereauReconcileResult(res);
                    }
                    fetchIntermediaries();
                  } catch (err: any) {
                    toast.error(err.response?.data?.message || 'Bordereau audit failed');
                  } finally {
                    setSubmittingBordereau(false);
                  }
                }}
                className="px-5 py-2 text-xs bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg shadow-sm flex items-center gap-1.5"
              >
                {submittingBordereau ? <RefreshCw size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
                Submit & Auto-Reconcile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
